import {
  BillingInformation,
  useSearchBillingInformation,
  useUpdateBillingAddressDetails,
  formatRoutingId,
  getRoutingIdType,
  useUpdateBillingInformationProvider,
} from '@app/api';
import { SubmitButton, TextLoadingPlaceholder } from '@app/core';
import { Theme } from '@app/theme';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { SearchInput, SubmitButtonsLine } from 'components';
import { formatVat } from 'i18n';
import classNames from 'classnames';
import { bi } from '@fullcalendar/core/internal-common';

export type DuplicationResolution =
  | {
      action: 'select';
      providerId: string;
      billingInformation?: BillingInformation;
    }
  | { action: 'create' };

interface Props {
  billingInformation: BillingInformation;
  onConfirm?: (billingInformation: BillingInformation) => void;
}

const useStyles = createUseStyles((theme: Theme) => ({
  title: {
    ...theme.fonts.h4,
    marginBottom: theme.marginBase * 2,
  },
  searchLine: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.marginBase * 2,
  },
  description: {
    ...theme.fonts.caption,
    marginTop: theme.marginBase,
  },

  options: {
    marginTop: theme.marginBase * 4,
    marginBottom: theme.marginBase * 4,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 2,
  },
  option: {
    borderRadius: theme.borderRadius.std,
    padding: theme.marginBase,
    border: {
      style: 'solid',
      width: 1,
      color: theme.colors.grayBorder,
    },
    cursor: 'pointer',
  },
  selectedOption: {
    borderColor: theme.colors.green,
    backgroundColor: theme.colors.greenMuted,
  },
  disabledOption: {
    opacity: 0.4,
  },
  organizationName: {
    ...theme.fonts.label,
  },
  infos: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  vat: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
  },
  routingId: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
  },
}));

const removeSpaces = (val: string) => val.replaceAll(' ', '');
const areSameInfo = (i1?: BillingInformation, i2?: BillingInformation) =>
  !!(i1?.id && i2?.id && i1.id === i2?.id) ||
  !!(i1?.providerId && i2?.providerId && i1?.providerId === i2?.providerId);

export const OrganizationSelectionStep = ({
  billingInformation,
  onConfirm,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [vat, setVat] = useState<string>(billingInformation.vatNumber || '');
  const [routingId, setRoutingId] = useState<string>(
    billingInformation.routingId || '',
  );

  const routingIdType = getRoutingIdType(billingInformation);

  const { mutate, isLoading: isSaving } = useUpdateBillingAddressDetails(
    billingInformation.id,
  );
  const { mutate: mutateProvider, isLoading: isSavingProvider } =
    useUpdateBillingInformationProvider(billingInformation.id);

  const { data: duplicates = [], isLoading } = useSearchBillingInformation(
    billingInformation.id,
    vat,
    routingId,
  );

  const [selection, setSelection] = useState<BillingInformation | null>(null);

  const creationDisabled =
    isLoading ||
    duplicates.some(
      (i) =>
        (vat && vat === i.vatNumber) ||
        (routingId && routingId === i.routingId),
    );

  const canConfirm = useMemo(() => {
    if (!selection) {
      return false;
    }

    if (areSameInfo(selection, billingInformation)) {
      if (billingInformation.providerId) {
        return true;
      }
      return !creationDisabled;
    }

    if (!duplicates.find((i) => areSameInfo(i, selection))) {
      return false;
    }

    return true;
  }, [selection, creationDisabled, duplicates, billingInformation]);

  const confirm = () => {
    if (!selection) {
      return;
    }
    const selectionIsCurrent = selection.id === billingInformation.id;
    const callbacks = { onSuccess: onConfirm };

    if (selectionIsCurrent) {
      return mutateProvider(
        {
          provider: selection.provider,
          providerId: undefined,
          providerAddressId: undefined,
        },
        {
          onSuccess: () =>
            mutate(
              {
                ...billingInformation,
                vatNumber: vat,
                routingId: routingId,
                billingOrganization: selection.billingOrganization,
              },
              callbacks,
            ),
        },
      );
    }
    mutateProvider(
      {
        provider: selection.provider,
        providerId: selection.providerId,
        providerAddressId: selection.providerAddressId,
      },
      {
        onSuccess: () =>
          mutate(
            {
              ...billingInformation,
              vatNumber: selection.vatNumber,
              routingId: selection.routingId,
              billingOrganization: selection.billingOrganization,
            },
            callbacks,
          ),
      },
    );
  };

  return (
    <div>
      <h2 className={classes.title}>
        {t('admin.billing.billingInfo.organizationSelection.title')}
      </h2>
      <div className={classes.searchLine}>
        <SearchInput
          value={formatVat(vat)}
          onChange={(val) => setVat(removeSpaces(val))}
          label={t('admin.billing.billingInfo.organizationSelection.vatLabel')}
          onClear={() => setVat('')}
        />
        <SearchInput
          value={formatRoutingId(routingIdType, routingId)}
          onChange={(val) => setRoutingId(removeSpaces(val))}
          label={t(
            'admin.billing.billingInfo.organizationSelection.siretLabel',
          )}
          onClear={() => setRoutingId('')}
        />
      </div>

      <div className={classes.options}>
        {isLoading && (
          <div className={classes.option}>
            <div className={classes.organizationName}>
              <TextLoadingPlaceholder sample="Tandemz" />
            </div>
            <div className={classes.infos}>
              <div className={classes.vat}>
                <TextLoadingPlaceholder sample="FR00 000 000 0000" />
              </div>
              <div className={classes.routingId}>
                <TextLoadingPlaceholder sample="000 000 000 00000" />
              </div>
            </div>
          </div>
        )}

        {duplicates.map((info) => (
          <div
            className={classNames(classes.option, {
              [classes.selectedOption]:
                info.id === selection?.id ||
                info.providerId === selection?.providerId,
            })}
            onClick={() => setSelection(info)}
            aria-selected={
              info.id === selection?.id ||
              info.providerId === selection?.providerId
            }
            role="option"
          >
            <div className={classes.organizationName}>
              {info.billingOrganization}
            </div>
            <div className={classes.infos}>
              <div className={classes.vat}>
                {formatVat(info.vatNumber || undefined)}
              </div>
              <div className={classes.routingId}>
                {formatRoutingId(routingIdType, info.routingId)}
              </div>
            </div>
          </div>
        ))}
        <div
          className={classNames(classes.option, {
            [classes.selectedOption]: billingInformation.id === selection?.id,
            [classes.disabledOption]: creationDisabled,
          })}
          onClick={() =>
            creationDisabled ? null : setSelection(billingInformation)
          }
          aria-disabled={creationDisabled}
          aria-selected={billingInformation.id === selection?.id}
          role="option"
        >
          <div className={classes.organizationName}>
            {t('admin.billing.billingInfo.organizationSelection.createNew', {
              organization: billingInformation.billingOrganization,
            })}
          </div>
          <div className={classes.infos}>
            <div className={classes.vat}>{formatVat(vat)}</div>
            <div className={classes.routingId}>
              {formatRoutingId(routingIdType, routingId)}
            </div>
          </div>
        </div>
      </div>
      <SubmitButtonsLine>
        <SubmitButton
          onClick={confirm}
          disabled={!canConfirm}
          small
          loading={isSaving || isSavingProvider}
          text={t('admin.billing.billingInfo.organizationSelection.confirm')}
        />
      </SubmitButtonsLine>
    </div>
  );
};
